/**
 * Patient-facing file upload for `file`-type intake questions.
 *
 * Hits the unauthenticated public-document endpoint that already exists
 * for the GLP-1 intake's photo upload flow, then PUTs the file directly
 * to the returned presigned S3 URL. Returns the canonical S3 key + the
 * metadata we want to surface in the response.
 */

import type { FileUploadValue } from '@/lib/intake/templates/types';

const API_BASE = (import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api').replace(/\/+$/, '');

interface UploadOptions {
  fileName: string;
  contentType: string;
  /** Called with 0–100 progress integers; emitted at coarse milestones. */
  onProgress?: (pct: number) => void;
}

export async function uploadIntakeFile(
  blob: Blob,
  { fileName, contentType, onProgress }: UploadOptions,
): Promise<FileUploadValue> {
  onProgress?.(10);
  const presignedRes = await fetch(`${API_BASE}/uploads/public/document`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      filename: fileName,
      contentType,
      documentType: 'intake_form_file',
    }),
  });
  const presignedJson = await presignedRes.json();
  if (!presignedRes.ok || !presignedJson?.success) {
    throw new Error(presignedJson?.message || 'Failed to get upload URL');
  }
  const { uploadUrl, key } = presignedJson.data;

  onProgress?.(50);
  const putRes = await fetch(uploadUrl, {
    method: 'PUT',
    body: blob,
    headers: {
      'Content-Type': contentType,
      'x-amz-server-side-encryption': 'AES256',
    },
  });
  if (!putRes.ok) {
    throw new Error(`S3 upload failed (${putRes.status})`);
  }
  onProgress?.(100);

  return {
    s3Key: key,
    fileName,
    contentType,
    uploadedAt: new Date().toISOString(),
  };
}
